import Layout from '../../components/layout/Layout';
import Header from './components/Header';
import Filters from './components/Filters';
import { useCallback, useEffect, useState } from 'react';
import CardHeaderDTO from '@src/adapters/dto/card/CardHeaderDTO';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import CardFilterDTO from '@src/adapters/dto/card/CardFilterDTO';
import { Grid } from '@mui/material';
import SearchResults from './components/results/SearchResults';
import CardInformation from './components/results/CardInformation';
import useArchiveCardLazy from '@src/adapters/hooks/card/useArchiveCard';
import useRestoreCardLazy from '@src/adapters/hooks/card/useRestoreCard';
import useGetCardsLazy from '@src/adapters/hooks/card/useGetCards';

function Cards() {
  const [isEdit, setIsEdit] = useState(false);
  const [selectedCard, setSelectedCard] = useState<CardHeaderDTO>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [hasModificationId, setHasModificationId] = useState('');
  const { execute: archive } = useArchiveCardLazy();
  const { execute: restore } = useRestoreCardLazy();
  const { execute: find } = useGetCardsLazy();
  const [recordCount, setRecordCount] = useState(0);
  const [cards, setCards] = useState<CardHeaderDTO[]>([]);

  const handleEdit = () => {
    setIsEdit(true);
  };

  const handleArchive = () => {
    setIsEdit(true);
    if (selectedCard) {
      archive({ id: selectedCard.id }).then((result) => {
        findCards();
        setSelectedCard(undefined);
      });
    }
  };

  const handleRestore = () => {
    setIsEdit(true);
    if (selectedCard) {
      restore({ id: selectedCard.id }).then((result) => {
        findCards();
        setSelectedCard(undefined);
      });
    }
  };

  const handleCardSelected = (card: CardHeaderDTO) => {
    setSelectedCard(card);
    setIsEdit(false);
  };

  const methods = useForm<CardFilterDTO>({
    mode: 'onChange',
    defaultValues: {
      currentPage: 0,
      pageSize: 10,
      sortAscending: false,
      sortKey: 'updated',
    },
  });
  const { control, setValue } = methods;

  const rawFilter = useWatch<CardFilterDTO>({
    control,
  });

  const filter = rawFilter as CardFilterDTO;

  useEffect(() => {
    if (selectedCard) {
      setCards((prev) =>
        prev.map((c) => (c.id === selectedCard.id ? selectedCard : c))
      );
    }
  }, [selectedCard]);

  useEffect(() => {
    setCards((prev) =>
      prev.map((c) =>
        c.id === hasModificationId ? { ...c, hasModifications: true } : c
      )
    );
  }, [hasModificationId]);

  const findCards = useCallback(() => {
    localStorage.setItem('cardFilter', JSON.stringify(filter));

    find({ filter }).then((result) => {
      setCards(result.cards);
      if (result.recordCount !== undefined) {
        setRecordCount(result.recordCount);
      }
    });
  }, [filter, find]);

  useEffect(() => {
    findCards();
  }, [filter, findCards]);

  const handleSort = (key: string) => {
    if (filter.sortKey === key) {
      setValue('sortAscending', !filter.sortAscending);
    } else {
      setValue('sortKey', key);
      setValue('sortAscending', true);
    }
  };

  const handlePreviewClicked = () => {
    setPreviewOpen(true);
  };

  const onHasModifications = () => {
    if (selectedCard) {
      setHasModificationId(selectedCard.id);
    }
  };

  return (
    <Layout>
      <FormProvider {...methods}>
        <Header
          isEdit={isEdit}
          onEdit={handleEdit}
          onArchive={handleArchive}
          onPreview={handlePreviewClicked}
          onRestore={handleRestore}
          selectedCard={selectedCard}
        />
        <Filters />
        <Grid container spacing={2}>
          <Grid item>
            <SearchResults
              cards={cards}
              filter={filter}
              recordCount={recordCount}
              selectedCard={selectedCard}
              onSort={handleSort}
              onCardSelected={handleCardSelected}
            />
          </Grid>
          <Grid item>
            {selectedCard && (
              <CardInformation
                cardId={selectedCard.id}
                isEdit={isEdit}
                previewOpen={previewOpen}
                onClosePreview={() => setPreviewOpen(false)}
                onViewClick={() => setPreviewOpen(true)}
                onHasModifications={onHasModifications}
              />
            )}
          </Grid>
        </Grid>
      </FormProvider>
    </Layout>
  );
}

export default Cards;
